import {
  Box,
  Button,
  Flex,
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  Tooltip,
  VisuallyHidden,
  useBoolean,
} from '@chakra-ui/react';
import styled from '@emotion/styled';
import { IMAGE_EDITED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { SliderNumberInput } from 'modules/common/components/Input';
import { ReactElement, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from 'theme';
import { colors } from 'theme/colors';
import { getColorWithAlphaString } from 'utils/color';
import { DropShadowName, ImageWidgetData } from 'widgets/ImageWidget/ImageWidget.types';
import { useWidget } from 'widgets/sdk';

import { DROP_SHADOW_LIST } from './ShadowOptions.config';

interface DropShadowOptionProps {
  label: string;
  value: DropShadowName;
  appliedDropShadowName: DropShadowName;
  filter: string;
  onClickDropShadow: (dropShadowName: DropShadowName) => void;
}

const DropShadowOption = memo(
  ({ label, value, appliedDropShadowName, filter, onClickDropShadow }: Readonly<DropShadowOptionProps>) => (
    <DropShadowSample
      as={'button'}
      p={2}
      pb={1}
      onClick={() => onClickDropShadow(value)}
      className={appliedDropShadowName === value ? 'active' : ''}
    >
      <Box
        w='48px'
        h='48px'
        border={`1.5px solid ${theme.colors.gray[200]}`}
        borderRadius={'base'}
        background={colors.white}
        filter={filter}
        mb={2}
      ></Box>
      <Text fontSize={'sm'} fontWeight={'medium'} color={colors.black} as={'span'}>
        {label}
      </Text>
    </DropShadowSample>
  ),
);

DropShadowOption.displayName = 'DropShadowOption';

const DropShadowOptionList = () => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'imageToolbar', useSuspense: false });
  const { updateWidget, filter = {} } = useWidget<ImageWidgetData>();
  const { appliedDropShadowName = 'None' } = filter;

  const handleClickDropShadow = (dropShadowName: DropShadowName) => {
    updateWidget({
      filter: {
        dropShadowIntensity: 50,
        appliedDropShadowName: dropShadowName,
      },
    });

    Mixpanel.track(IMAGE_EDITED.eventName, {
      image_effect: IMAGE_EDITED.image_effect.DropShadow,
    });
  };

  return (
    <Flex pt={2} pb={2} px={2} flexWrap='wrap' rowGap='0.625rem' justifyContent={'space-around'}>
      {DROP_SHADOW_LIST.map((item) => (
        <DropShadowOption
          key={`drop_shadow_item_${item.label}`}
          label={t(item.labeli18n)}
          value={item.label}
          filter={item.filter}
          appliedDropShadowName={appliedDropShadowName}
          onClickDropShadow={handleClickDropShadow}
        />
      ))}
    </Flex>
  );
};

const ShadowIntensityField = () => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'imageToolbar', useSuspense: false });

  const { updateWidget, filter = {} } = useWidget<ImageWidgetData>();
  const { appliedDropShadowName = 'None', dropShadowIntensity = 50 } = filter;

  const handleChangeDropShadowIntensity = (intensity: number) => {
    updateWidget({ filter: { dropShadowIntensity: intensity } });
  };

  return appliedDropShadowName !== 'None' ? (
    <SliderNumberInput
      title={t('shadow.sliderLabel')}
      value={dropShadowIntensity}
      onChange={handleChangeDropShadowIntensity}
      minValue={0}
      maxValue={100}
      step={1}
      suffix={'%'}
      sliderWidth={120}
      containerProps={{ py: 2, px: 3, pt: 3 }}
    />
  ) : (
    <></>
  );
};

export const ShadowOptions = memo((): ReactElement => {
  const [, setIsOpen] = useBoolean(false);
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'imageToolbar', useSuspense: false });
  const label = t('shadow.label');

  return (
    <Popover isLazy onClose={setIsOpen.off} placement='bottom-start'>
      <PopoverTrigger>
        <Box>
          <Tooltip hasArrow label={t('shadow.dropShadow')}>
            <Button
              variant='ghost-with-active'
              data-testid='image-widget-shadow-menu-trigger-button'
              fontWeight='semibold'
              onClick={setIsOpen.toggle}
              size='sm'
            >
              {label}
            </Button>
          </Tooltip>
        </Box>
      </PopoverTrigger>
      <PopoverContent w='19rem' boxShadow='md' padding={0} data-testid='image-widget-shadow-menu-content'>
        <VisuallyHidden>
          <PopoverHeader>{label}</PopoverHeader>
        </VisuallyHidden>
        <DropShadowOptionList />
        <ShadowIntensityField />
      </PopoverContent>
    </Popover>
  );
});

ShadowOptions.displayName = 'ShadowOptions';

const DropShadowSample = styled(Box)`
  border: 1.5px solid transparent;
  border-radius: 4px;

  &:hover {
    background: ${getColorWithAlphaString(colors.hover.gray, 0.08)};
  }
  &.active {
    background: ${getColorWithAlphaString(colors.hover.blue, 0.08)};
    span.chakra-text {
      color: ${colors.upgrade.blue[700]};
    }
  }
`;

export default ShadowOptions;
